import React, { useEffect, useRef, useState } from 'react';
import { Snackbar, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { discoveryApi } from '../../api/discovery';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
const excluded = (type) => ['NOT_INTERESTED', 'ALREADY_KNOWN'].includes(type);
export function useTrackFeedbackUndo(onRestore) {
  const client = useQueryClient(),
    userId = useUserStore((s) => s.user?.id);
  const { locale } = useWebLocale(),
    en = locale === 'en';
  const [notice, setNotice] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(false);
  const serial = useRef(0);
  const current = useRef(null),
    lock = useRef(false);
  useEffect(() => {
    current.current = null;
    setNotice(null);
    setBusy(false);
    setError(false);
    lock.current = false;
    return () => {
      current.current = null;
    };
  }, [userId]);
  const applied = (item, type, index) => {
    const next = {
      id: ++serial.current,
      item,
      type,
      index,
      userId,
      token: localStorage.getItem('POSTPOP_AUTH_TOKEN'),
    };
    current.current = next;
    setNotice(next);
    setError(false);
    setBusy(false);
  };
  const undo = async () => {
    const target = current.current;
    if (!target?.type || lock.current || target.userId !== useUserStore.getState().user?.id) return;
    const valid = () =>
      current.current === target &&
      useUserStore.getState().user?.id === target.userId &&
      localStorage.getItem('POSTPOP_AUTH_TOKEN') === target.token;
    lock.current = target;
    setBusy(true);
    setError(false);
    try {
      await client.cancelQueries({
        queryKey: ['web-discovery-feedback', userId, 'tracks', target.item.id],
      });
      if (!valid()) return;
      await discoveryApi.clearFeedback('tracks', target.item.id);
      if (!valid()) return;
      client.setQueryData(['web-discovery-feedback', userId, 'tracks', target.item.id], {
        feedbackType: null,
      });
      if (excluded(target.type)) {
        client.setQueriesData({ queryKey: ['web-discovery', userId] }, (data) => {
          if (!data?.excludedTrackIds?.includes(target.item.id)) return data;
          return {
            ...data,
            excludedTrackIds: data.excludedTrackIds.filter((id) => id !== target.item.id),
            tracks: [target.item, ...(data.tracks || []).filter((t) => t.id !== target.item.id)],
          };
        });
        onRestore(target.item, target.index);
      }
      void client.invalidateQueries({ queryKey: ['web-discovery', userId], refetchType: 'none' });
      const cleared = { ...target, type: null };
      current.current = cleared;
      setNotice(cleared);
      setBusy(false);
    } catch {
      if (valid()) setError(true);
    } finally {
      if (lock.current === target) lock.current = false;
      if (valid()) setBusy(false);
    }
  };
  const text = error
    ? en
      ? 'Could not undo. Please try again.'
      : '취소하지 못했어요. 다시 시도해 주세요.'
    : !notice?.type
      ? en
        ? 'Recommendation choice cleared.'
        : '추천 조정을 취소했어요.'
      : notice.type === 'MORE_LIKE_THIS'
        ? en
          ? 'We’ll show more music like this.'
          : '비슷한 음악을 더 보여드릴게요.'
        : notice.type === 'ALREADY_KNOWN'
          ? en
            ? 'Marked as a track you already know.'
            : '이미 아는 곡으로 반영했어요.'
          : en
            ? 'We’ll show less music like this.'
            : '취향이 아닌 곡으로 반영했어요.';
  const toast = (
    <Snackbar
      key={notice?.id}
      className="pp-track-feedback-notice"
      open={!!notice}
      autoHideDuration={busy || error ? null : 2800}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={(_, reason) => {
        if (reason === 'clickaway' || busy || error) return;
        current.current = null;
        setNotice(null);
      }}
    >
      <div role={error ? 'alert' : 'status'} className="pp-track-feedback-notice-body">
        <span>{text}</span>
        {notice?.type && (
          <Button disabled={busy} onClick={undo}>
            {busy
              ? en
                ? 'Undoing…'
                : '취소 중…'
              : error
                ? en
                  ? 'Retry'
                  : '다시 시도'
                : en
                  ? 'Undo'
                  : '실행 취소'}
          </Button>
        )}
      </div>
    </Snackbar>
  );
  return { applied, busy, toast: notice && notice.userId === userId ? toast : null };
}
